import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    Button,
    Space,
    Typography,
    message,
    Tag,
    Empty,
    Tooltip
} from 'antd';
import {
    HeartFilled,
    HeartOutlined,
    EyeOutlined
} from '@ant-design/icons';
import moment from 'moment';
import favoriteService from '../../services/favoriteService';
import DashboardLayout from '../Dashboard/DashboardLayout';
import PageHeader from '../Common/PageHeader';
import { getAuctionStartTimeStatus } from '../../utils/dateUtils';

const { Title } = Typography;

const FavoriteAuctions = () => {
    const [auctions, setAuctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const navigate = useNavigate();

    useEffect(() => {
        loadFavoriteAuctions();
    }, [pagination.current, pagination.pageSize]);

    const loadFavoriteAuctions = async () => {
        try {
            setLoading(true);
            const response = await favoriteService.getFavoriteAuctions({
                page: pagination.current,
                per_page: pagination.pageSize
            });
            
            setAuctions(response.data);
            setPagination(prev => ({
                ...prev,
                total: response.meta.total
            }));
        } catch (error) {
            console.error('Error loading favorite auctions:', error);
            message.error('Failed to load favorite auctions');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleFavorite = async (auction) => {
        try {
            // Optimistically update the UI
            setAuctions(prevAuctions => prevAuctions.filter(a => a.id !== auction.id));
            setPagination(prev => ({
                ...prev,
                total: prev.total - 1
            }));
            
            // Make the API call
            await favoriteService.toggleFavoriteAuction(auction.id);
            //message.success('Removed from favorites');
        } catch (error) {
            // Revert the optimistic update on error
            setAuctions(prevAuctions => [...prevAuctions, auction]);
            setPagination(prev => ({
                ...prev,
                total: prev.total + 1
            }));
            console.error('Error toggling favorite:', error);
            message.error('Failed to remove from favorites');
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            draft: 'default',
            scheduled: 'processing',
            active: 'success',
            completed: 'success',
            cancelled: 'error'
        };
        return colors[status] || 'default';
    };

    const handleTableChange = (pagination) => {
        setPagination(pagination);
    };

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            width: 200,
            render: (text, record) => (
                <Tooltip title={text} placement="top">
                    <a 
                        href={`/auctions/${record.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                            color: '#1890ff',
                            textDecoration: 'none',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.textDecoration = 'none';
                        }}
                    >
                        {text.length > 30 ? `${text.substring(0, 30)}...` : text}
                    </a>
                </Tooltip>
            )
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            width: 80,
            render: (category) => (
                <Tag>
                    {category?.name || 'N/A'}
                </Tag>
            )
        },
        {
            title: 'Start Time',
            dataIndex: 'start_time',
            key: 'start_time',
            width: 100,
            render: (date) => {
                const { statusText, statusColor, formattedDate, tooltipText } = getAuctionStartTimeStatus(date);
                
                return (
                    <Tooltip title={tooltipText}>
                        <div>
                            <div style={{ marginBottom: 2 }}>
                                {formattedDate}
                            </div>
                            {statusText && (
                                <Tag color={statusColor} size="small" style={{ marginTop: 4 }}>
                                    {statusText}
                                </Tag>
                            )}
                        </div>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 60,
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {status.toUpperCase()}
                </Tag>
            )
        },
        {
            title: 'Lots',
            dataIndex: 'lots_count',
            key: 'lots_count',
            width: 60,
            render: (count) => count || 0
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 180,
            render: (_, record) => {
                const now = moment();
                const startTime = moment(record.start_time);
                const isToday = startTime.isSame(moment(), 'day');
                const showConnectToBid = isToday && record.status === 'active';
                
                const handleConnectToBid = (e) => {
                    e.stopPropagation();
                    // Auction is today and active - open bidding in popup
                    const newWindow = window.open(
                        `/live-auctions/${record.slug}`,
                        '_blank',
                        'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=1920,height=1080'
                    );
                    // Focus the new window
                    if (newWindow) {
                        newWindow.focus();
                    }
                };

                return (
                    <Space>
                        <Button
                            type="text"
                            icon={<HeartFilled style={{ color: '#ff4d4f' }} />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleToggleFavorite(record);
                            }}
                            title="Remove from favorites"
                        />
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/auctions/${record.slug}`);
                            }}
                            title="View auction details"
                        />
                        {showConnectToBid && (
                            <Button
                                type="primary"
                                size="small"
                                onClick={handleConnectToBid}
                                style={{
                                    borderRadius: '8px',
                                    fontWeight: 600,
                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    border: 'none',
                                    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
                                    animation: 'pulse 2s ease-in-out infinite'
                                }}
                            >
                                Enter Auction
                            </Button>
                        )}
                    </Space>
                );
            }
        }
    ];

    return (
        <DashboardLayout>
            <PageHeader
                title="Favorite Auctions"
                description="View and manage your favorite auctions"
            />
            <Table
                columns={columns}
                dataSource={auctions}
                rowKey="id"
                pagination={pagination}
                loading={loading}
                onChange={handleTableChange}
                scroll={{ x: 1200 }}
                locale={{
                    emptyText: loading 
                        ? null // Don't show empty text when loading
                        : (
                            <Empty
                                description="No favorite auctions yet"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        )
                }}
            />
            <style>{`
                @keyframes pulse {
                    0%, 100% {
                        box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3), 0 0 0 0 rgba(24, 144, 255, 0.3);
                        transform: scale(1);
                    }
                    50% {
                        box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3), 0 0 0 4px rgba(24, 144, 255, 0.1);
                        transform: scale(1.02);
                    }
                }
            `}</style>
        </DashboardLayout>
    );
};

export default FavoriteAuctions; 